import React from 'react';
import { ModelTemplate } from '../types';

interface ModelGridProps {
    models: ModelTemplate[];
    loading: boolean;
    selectedId: string | null;
    onSelect: (model: ModelTemplate) => void;
    genderFilter: 'female' | 'male';
    onGenderChange: (gender: 'female' | 'male') => void;
}

export const ModelGrid: React.FC<ModelGridProps> = ({
    models,
    loading,
    selectedId,
    onSelect,
    genderFilter,
    onGenderChange,
}) => {
    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Models Gallery</h3>
                <div className="flex bg-gray-100 rounded-full p-1">
                    <button
                        onClick={() => onGenderChange('female')}
                        className={`px-4 sm:px-6 py-2 rounded-full text-sm font-medium ${genderFilter === 'female' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Women
                    </button>
                    <button
                        onClick={() => onGenderChange('male')}
                        className={`px-4 sm:px-6 py-2 rounded-full text-sm font-medium ${genderFilter === 'male' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        Men
                    </button>
                </div>
            </div>

            {loading && (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
                </div>
            )}

            {!loading && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
                    {models
                        .filter((m) => m.gender === genderFilter)
                        .map((model) => {
                            const isSelected = selectedId === model.id;
                            return (
                                <button
                                    key={model.id}
                                    onClick={() => onSelect(model)}
                                    className={`group relative text-left transition-all ${isSelected ? 'scale-[0.98]' : 'hover:scale-[1.02]'}`}
                                >
                                    <div className={`relative aspect-[3/2] rounded-xl sm:rounded-2xl overflow-hidden ${isSelected ? 'ring-4 ring-secondary-500' : ''}`}>
                                        <img
                                            src={model.imageUrl}
                                            alt={model.name}
                                            className="w-full h-full object-contain bg-gray-100 group-hover:scale-105 transition-transform duration-300"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x400?text=Model';
                                            }}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-60 group-hover:opacity-90 transition-opacity" />
                                        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                                            <p className="text-sm sm:text-base font-semibold text-white">{model.name}</p>
                                        </div>
                                        {isSelected && (
                                            <div className="absolute top-3 right-3 bg-secondary-500 text-white rounded-full p-1.5 shadow-lg">
                                                <i className="fas fa-check text-xs"></i>
                                            </div>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                </div>
            )}
        </div>
    );
};
